import { Controller } from '@hotwired/stimulus'

/**
 * Controller for managing a list of dynamic items.
 * Allows adding items by cloning a template item, removing individual items via clicks,
 * and showing or hiding a message when the list is empty.
 *
 * Functionality:
 * - Adds items from a text input, cloning a template element and setting its content.
 * - Removes items via a delete icon.
 * - Displays an "empty" message when there are no items and hides it when items are added.
 * - Supports pre-existing items loaded from the server.
 *
 *
 * Basic HAML example:
 * %div{ data: { controller: 'item-list' } }
 *   %input{ data: { item_list_target: 'mainInput' } }
 *   %button{ data: { action: 'click->item-list#addElement' } }
 *     'Add element'
 *   %div{ data: { item_list_target: 'container' } }
 *     %p{ data: { item_list_target: 'emptyMessage' } } No items
 *     %div{ data: { item_list_target: 'blockTemplate' }, class: 'hidden element' }
 *       %p{ id: 'content' }
 *       %input{ id: 'hiddenField', type: 'hidden' }
 */
export default class extends Controller {
  static targets = ['mainInput', 'container', 'emptyMessage', 'itemAlreadyAddedError', 'blockTemplate', 'addedBlock']

  connect() {
    this.toggleEmptyMessage();
  }

  addElement() {
    const mainInputValue = this.mainInputTarget?.value?.trim();
    
    if (!mainInputValue) return;
    
    if (this.addedBlockTargets.some(element => element.textContent.trim() == mainInputValue)) {
      this.itemAlreadyAddedErrorTarget.classList.remove('hidden');
      return;
    } else {
      this.itemAlreadyAddedErrorTarget.classList.add('hidden');
    }
    
    const newBlock = this.blockTemplateTarget.cloneNode(true);
    const content = newBlock.querySelector('p#content');
    const hiddenInput = newBlock.querySelector('input#hiddenField');
    
    hiddenInput.value = mainInputValue;
    content.innerText = mainInputValue;
    newBlock.dataset.itemListTarget = 'addedBlock';
    newBlock.classList.remove('hidden');
    this.mainInputTarget.value = '';
    this.containerTarget.appendChild(newBlock);
    this.toggleEmptyMessage();
  }

  removeElement(e) {
    const element = e.target.closest('div.element');
    
    if (element) {
      element.remove();
      this.toggleEmptyMessage();
    }
  }

  toggleEmptyMessage() {
    this.emptyMessageTarget.classList.toggle('hidden', this.addedBlockTargets.length > 0);
  }
}
